import { createContext, useContext } from 'react'
import type { PingDescription } from './pingAgent'

/**
 * HOW A `ping-agent:` CHIP LEARNS WHO IT IS ABOUT, AND WHERE A TAP GOES.
 *
 * Owner, 2026-09-21: the tag must read as the agent's name at its machine (or at
 * its owner, for a friend's), in Clean View, Project Chat and Friend Chat — and
 * 「假設使用者點擊這個…字,如果這個 Agent 是他自己的話,那也可以跳轉過去這個 Agent 他的
 * session」.
 *
 * A CONTEXT, for the reason OpenDocContext is one (files/openDoc.ts, the first in
 * this codebase): the chip is rendered by the markdown extension deep inside
 * Clean View (AgentPanel → the turn renderers → Msg → MsgBody) and again inside
 * the chat's Message, and the two things it needs — my roster, and App's jump —
 * both live at the top. The alternative was the same two values written into a
 * dozen signatures for something that never changes.
 *
 * Project Chat and Friend Chat come free together: both render the same
 * `Message` component, so one provider covers all three surfaces.
 *
 * `null` means nothing above resolved it — a test, or a surface with no provider.
 * The chip then falls back to the label the tag itself carried, which is still
 * better than the raw address, and does not offer a jump it cannot make.
 */
export type PingContextValue = {
  /** Words for one tag: name@machine (mine) or name@owner (a friend's). */
  resolve: (hash: string, label: string) => PingDescription
  /**
   * Open that agent's own conversation. Called ONLY for a chip the resolver
   * marked `mine` — there is no session of a friend's agent for this account to
   * open, and a chip that looks tappable and does nothing is a dead promise.
   */
  open: (a: { session: string; host: string | null }) => void
}

export const PingContext = createContext<PingContextValue | null>(null)

export function usePing(): PingContextValue | null {
  return useContext(PingContext)
}
